/**
 * SnoozeDialog - Modal for snoozing a member's ECM reminders
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PauseCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { SnoozeReason, SnoozeCreateRequest } from '@/services/api/ecmReminders';

interface SnoozeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: number;
  memberName: string;
  enrollmentId?: number;
  onSnoozeCreated: () => void;
  createSnooze: (data: SnoozeCreateRequest) => Promise<any>;
}

const REASON_OPTIONS: { value: SnoozeReason; label: string; description: string }[] = [
  { value: 'hospitalized', label: 'Hospitalized', description: 'Member is currently hospitalized' },
  { value: 'unreachable', label: 'Unreachable', description: 'Unable to contact member after multiple attempts' },
  { value: 'vacation', label: 'Vacation / Travel', description: 'Member is temporarily away' },
  { value: 'incarcerated', label: 'Incarcerated', description: 'Member is currently incarcerated' },
  { value: 'member_request', label: 'Member Request', description: 'Member requested a pause in outreach' },
  { value: 'pending_transfer', label: 'Pending Transfer', description: 'Member is being transferred to another provider' },
  { value: 'bereavement', label: 'Bereavement', description: 'Member is dealing with a family loss' },
  { value: 'other', label: 'Other', description: 'Other reason (please specify)' },
];

const DURATION_PRESETS = [7, 14, 30, 60, 90];

export const SnoozeDialog: React.FC<SnoozeDialogProps> = ({
  isOpen,
  onClose,
  memberId,
  memberName,
  enrollmentId,
  onSnoozeCreated,
  createSnooze,
}) => {
  const [reason, setReason] = useState<SnoozeReason | ''>('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [snoozeDays, setSnoozeDays] = useState(14);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a reason');
      return;
    }

    setSubmitting(true);
    try {
      const result = await createSnooze({
        member_id: memberId,
        enrollment_id: enrollmentId,
        reason,
        reason_details: reasonDetails || undefined,
        snooze_days: snoozeDays,
      });

      if (result) {
        toast.success(`Reminders snoozed for ${memberName}`, {
          description: `Paused for ${snoozeDays} days. Reason: ${REASON_OPTIONS.find(r => r.value === reason)?.label}`,
        });
        onSnoozeCreated();
        onClose();
        // Reset form
        setReason('');
        setReasonDetails('');
        setSnoozeDays(14);
      } else {
        toast.error('Failed to create snooze');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to create snooze';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PauseCircle className="w-5 h-5 text-gray-600" />
            Snooze Reminders
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Member info */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <span className="text-sm text-gray-500">Member:</span>
            <span className="text-sm font-medium">{memberName}</span>
          </div>

          {/* Reason selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {REASON_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setReason(opt.value)}
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    reason === opt.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Details {reason === 'other' && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              placeholder="Optional notes about the snooze reason..."
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none h-20 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={500}
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Duration: <span className="font-bold text-blue-600">{snoozeDays} days</span>
            </label>
            <div className="flex gap-2 mb-2">
              {DURATION_PRESETS.map((d) => (
                <Button
                  key={d}
                  type="button"
                  variant={snoozeDays === d ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setSnoozeDays(d)}
                  className="text-xs"
                >
                  {d}d
                </Button>
              ))}
            </div>
            <input
              type="range"
              min={1}
              max={90}
              value={snoozeDays}
              onChange={(e) => setSnoozeDays(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>1 day</span>
              <span>90 days</span>
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Snoozing will cancel all pending reminders for this member and pause new reminder
              generation. The snooze can be cancelled early at any time.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!reason || submitting || (reason === 'other' && !reasonDetails)}
            >
              {submitting ? 'Snoozing...' : `Snooze for ${snoozeDays} days`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
